/* eslint-disable consistent-return */
import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import faker from 'faker';
import { concat, includes, map, values } from 'lodash';

import { FeaturePoint } from '../SchemaUtilities/GeoJSONSchema';
import {
  notesDataSchema,
  settingsAdvancedMapDataSchema
} from '../SchemaUtilities/AdvancedMapSchema';

import {
  SPATIAL_SUBTYPES,
  JOIN_SUBTYPES,
  GEOJSON_API_BASED_SUBTYPES,
  RASTER_3D_MAPS_SUBTYPES
} from '../Layers/Layers';

import requiredIf from '../../modules/schema-conditional-required';

/**
 * A map object for ipsilum maps.
 *
 * @typedef {object} Map
 * @property {string[]} [baseMaps] - An array contaning the names of the base maps selected for this map (needed in ias).
 * @property {Array} [baseMapsTransparency] - An array containing the base maps that need a different transparency value.
 * @property {Array} [baseMapsZoom] - An array containing the base maps that need a different zoom value.
 * @property {string} createdAt - The date when it was created.
 * @property {string} [description] - The description for the map.
 * @property {string} deleted - Indicates whether it is in the trash or not.
 * @property {string} [esriWebMapId] - The id that this map has in esri (needed in esri).
 * @property {string} _id - The map id.
 * @property {string} [imageUrl] - The url for the image thumbnail.
 * @property {string[]} [layers] - An array contaning the Ids of the layers that make out this map (needed in ias).
 * @property {Array} [layersTransparency] - An array containing the layers that need a different transparency value.
 * @property {Array} [layersZoom] - An array containing the layers that need a different zoom value.
 * @property {boolean} layersOverlayed - Indicates whether the layers have a checkbox or radio control.
 * @property {string} [logoUrl] - The utl of the logo on the map.
 * @property {FeaturePoint} [mapLocation] - The map center for the layer.
 * @property {string} mapType - The type of map you are working with. One of ['ias', 'esri'].
 * @property {string} name - The name for the map.
 * @property {string} owner - The owner id.
 * @property {string} [pageUrl] - The link where it redirects after clicking the logo on the map.
 * @property {string} publicId - The public key generated for the URL.
 * @property {selectedButtonData} selectedButtons - The selected custom buttons to be shown over the map.
 * @property {settingsAdvancedMapData} settingsAdvancedMap - The configuration of advanced map components.
 * @property {boolean} shared - Indicates whether it is being shared or not.
 * @property {boolean} sharedPrivate - Indicates whether it is being shared private or not.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {boolean} withLocation - Indicates whether it is being location or not.
 * @property {boolean} withLocationPrivate - Indicates whether it is being location private or not.
 */

/**
 * An object that defines the configuration of advanced map components.
 *
 * @typedef {object} settingsAdvancedMapData
 * @property {object} directButtons - The list of de annotation Templates.
 * @property {object} components - The components of the advanced map.
 * @property {object} connections - The connections of the advanced map.
 * @property {string} defaultComponent - The first component to show when open advanced map.
 * @property {number} sizeDrawer - The size of the drawer.
 * @property {object} textSettings - The settings of the text.
 * @property {object} buttonSettings - The settings of the text.
 * @property {Array} mapTypes - Maps.
 * @property {Array} viewLinks - The list of the links to show.
 * @property {notesDataSchema} notes - The settings of the notes component.
 * @property {boolean} showTooltip - The settings of the notes component.
 *
 */

/**
 * An object that defines which buttons to show in the map.
 *
 * @typedef {object} selectedButtonData
 * @property {boolean} Attribution - Show or hide the Attribution button.
 * @property {boolean} BaseMapSelector - Show or hide the BaseMapSelector button.
 * @property {boolean} Home - Show or hide the Home button.
 * @property {boolean} LayerEditor - Show or hide the component LayerEditorMaps.
 * @property {boolean} LayerSelector - Show or hide the LayerSelector button.
 * @property {boolean} Legend - Show or hide the Legend button.
 * @property {boolean} PopupInformation - Show or hide the popup.
 * @property {boolean} QRCode - Show or hide the QRCode button.
 * @property {boolean} Search - Show or hide the Search button.
 * @property {boolean} Scale - Show or hide the Scale button.
 * @property {boolean} Title - Show or hide the title button.
 * @property {boolean} ViewHtml - Show information in html or text.
 * @property {boolean} Zoom - Show or hide the Zoom button.
 */

/**
 * An object that defines which map type to show in the selector.
 *
 * @typedef {object} selectedMapType
 * @typedef {object} layoutJson
 * @typedef {object} templates
 * @property {boolean} Leaflet - 2D Leaflet map type.
 * @property {boolean} Cesium - 3D Cesium map type.
 */

export const MAP_TYPES = {
  LEAFLET: 'leaflet',
  CESIUM: 'cesium',
  properties: {
    LEAFLET: {
      name: 'LEAFLET',
      value: 'leaflet',
      humanValue: '2D'
    },
    CESIUM: {
      name: 'CESIUM',
      value: 'cesium',
      humanValue: '3D'
    }
  }
};

export const getLayerSubTypesForMapTypes = mapType => {
  switch (mapType) {
    case MAP_TYPES.LEAFLET:
      return concat(values(SPATIAL_SUBTYPES), values(JOIN_SUBTYPES));
    case MAP_TYPES.CESIUM:
      return concat(RASTER_3D_MAPS_SUBTYPES, GEOJSON_API_BASED_SUBTYPES);
    default:
      return [];
  }
};

/**
 * An object that defines which map type to show in the selector.
 *
 * @typedef {object} selectedMapType
 * @typedef {object} layoutJson
 * @typedef {object} templates
 * @property {boolean} isFeature - Advanced map or not.
 */

export const MAP_OPTIONS = {
  IsFeature: 'isFeature',
  properties: {
    isFeature: {
      name: 'IsFeature',
      value: 'isFeature',
      humanValue: 'Advanced Map'
    }
  }
};

const Maps = new Mongo.Collection('Maps');

Maps.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Maps.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const selectedButtonDataSchema = new SimpleSchema({
  Attribution: {
    type: Boolean,
    label: 'Flag that checks if the company logo is shown'
  },
  BaseMapSelector: {
    type: Boolean,
    label: 'Flag that checks if the base map selector is shown'
  },
  DrawControl: {
    type: Boolean,
    label: 'Flag that checks if the Draw Control selector is shown'
  },
  Home: {
    type: Boolean,
    label: 'Flag that checks if the home button is shown'
  },
  LayerEditor: {
    type: Boolean,
    label: 'Flag that checks if the LayerEditor button is shown'
  },
  LayerSelector: {
    type: Boolean,
    label: 'Flag that checks if the layer selector is shown'
  },
  Legend: {
    type: Boolean,
    label: 'Flag that checks if the legend is shown'
  },
  PopupInformation: {
    type: Boolean,
    label: 'Flag that checks if the PopupInformation button is shown'
  },
  QRCode: {
    type: Boolean,
    label: 'Flag that checks if the QR Code is shown'
  },
  Scale: {
    type: Boolean,
    label: 'Flag that checks if the scale is shown'
  },
  Search: {
    type: Boolean,
    label: 'Flag that checks if the search button is shown'
  },
  Title: {
    type: Boolean,
    label: 'Flag that checks if the title button is shown'
  },
  ViewHtml: {
    type: Boolean,
    label: 'Flag that checks if the ViewHtml button is shown'
  },
  Zoom: {
    type: Boolean,
    label: 'Flag that checks if the zoom controls are shown'
  }
});

const layoutJsonChild = new SimpleSchema({
  id: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the ID of the child array'
  },
  name: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the name of a schema'
  },
  width: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the width of the child array'
  },
  height: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the height of the child array'
  },
  componente: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the component of the child array'
  },
  color: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the component of the child array'
  },
  arriba: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the arriba of the child array'
  },
  arribasize: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the arribasize of the child array'
  },
  izquierda: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the left of the child array'
  }
});

const layoutJsonSchema = new SimpleSchema({
  id: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the ID of a schema'
  },
  name: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the name of a schema'
  },
  color: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the color of a schema'
  },
  width: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the width of a schema'
  },
  height: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the height of a schema'
  },
  componente: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the component of a schema'
  },
  child: {
    type: Array,
    label: 'This is the child array of a schema'
  },
  arriba: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the arriba of the child array'
  },
  arribasize: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the arribasize of the child array'
  },
  izquierda: {
    type: SimpleSchema.oneOf(Number),
    label: 'This is the left of the child array'
  },
  'child.$': {
    type: layoutJsonChild,
    label: 'layoutJsonChild'
  }
});

const templatesChild = new SimpleSchema({
  id: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the ID of the child array'
  },
  type: {
    type: SimpleSchema.oneOf(String),
    label: 'This is the type'
  },
  name: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the name of a schema'
  },
  template: {
    type: layoutJsonSchema,
    label: 'This is the child array of a schema'
  }
});

const templatesSchema = new SimpleSchema({
  id: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the ID of a schema'
  },
  name: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'This is the name of a schema'
  },
  child: {
    type: Array,
    label: 'This is the child array of a schema'
  },
  'child.$': {
    type: templatesChild,
    label: 'templatesChild'
  }
});

Maps.schema = new SimpleSchema({
  backgroundColor: {
    type: String,
    optional: true,
    label: 'The background color of the map.'
  },
  baseMaps: {
    type: Array,
    label: 'This is an array contains the names of the base maps selected for this map',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('mapType').value !== 'esri',
        schemaInstance: this
      });
    }
  },
  'baseMaps.$': {
    type: String,
    label: 'This is the Name of the BaseMapLayer'
  },
  baseMapsTransparency: {
    type: Array,
    label: 'An array containing the base maps that need a different opacity value',
    optional: true
  },
  'baseMapsTransparency.$': {
    type: Object,
    label: 'An object containing the id of the base map and the transparency value'
  },
  'baseMapsTransparency.$.id': {
    type: String,
    label: 'This is the ID of one base map included in the map'
  },
  'baseMapsTransparency.$.value': {
    type: Number,
    label: 'The new transparency value for the base map'
  },
  baseMapsZoom: {
    type: Array,
    label: 'An array containing the base maps that need a different zoom value',
    optional: true
  },
  'baseMapsZoom.$': {
    type: Object,
    label: 'An object containing the id of the base map and the zoom value'
  },
  'baseMapsZoom.$.id': {
    type: String,
    label: 'This is the ID of one base map included in the map'
  },
  'baseMapsZoom.$.maxZoom': {
    type: Number,
    label: 'The new maximum zoom value for the base map'
  },
  'baseMapsZoom.$.minZoom': {
    type: Number,
    label: 'The new minimum zoom value for the base map'
  },
  createdAt: {
    type: String,
    label: 'The date this map was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the map was deleted.',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  description: {
    type: String,
    label: 'A description of the map.',
    optional: true
  },
  esriWebMapId: {
    type: String,
    label: 'The id that this map has in esri',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('mapType').value === 'esri',
        schemaInstance: this
      });
    }
  },
  isFeature: {
    type: Boolean,
    label: 'The layer is from a Feature',
    optional: true
  },
  imageUrl: {
    type: String,
    optional: true,
    label: 'The url of an image to use as thumbnail'
  },
  layers: {
    type: Array,
    label: 'This is an array contaning the Ids of the layers that make out this map',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('mapType').value !== 'esri',
        schemaInstance: this
      });
    }
  },
  'layers.$': {
    type: String,
    label: 'This is the ID of one layer included in the map'
  },
  layersTransparency: {
    type: Array,
    label: 'An array containing the layers that need a different opacity value',
    optional: true
  },
  'layersTransparency.$': {
    type: Object,
    label: 'An object containing the id of the layer and the transparency value'
  },
  'layersTransparency.$.id': {
    type: String,
    label: 'This is the ID of one layer included in the map'
  },
  'layersTransparency.$.value': {
    type: Number,
    label: 'The new transparency value for the layer'
  },
  layersZoom: {
    type: Array,
    label: 'An array containing the layers that need a different zoom value',
    optional: true
  },
  'layersZoom.$': {
    type: Object,
    label: 'An object containing the id of the layer and the zoom value'
  },
  'layersZoom.$.id': {
    type: String,
    label: 'This is the ID of one layer included in the map'
  },
  'layersZoom.$.maxZoom': {
    type: Number,
    label: 'The new maximum zoom value for the layer'
  },
  'layersZoom.$.minZoom': {
    type: Number,
    label: 'The new minimum zoom value for the layer'
  },
  layersOverlayed: {
    type: Boolean,
    label: 'Indicates whether the layers control has a checkbox or radio control',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes([MAP_TYPES.LEAFLET], this.field('mapType').value),
        schemaInstance: this
      });
    }
  },
  layoutJson: {
    type: layoutJsonSchema,
    label: 'An object where the map settings are to be saved',
    optional: true
  },
  logoUrl: {
    type: String,
    label: 'The url of the logo',
    optional: true
  },
  mapLocation: {
    type: FeaturePoint,
    optional: true,
    label: 'The location of the map for the map.'
  },
  name: {
    type: String,
    label: 'A name to identify the map.'
  },
  mapType: {
    type: String,
    label: 'The type of map you are working with',
    allowedValues: ['ias', 'esri', ...map(MAP_TYPES.properties, 'value')]
  },
  owner: {
    type: String,
    label: 'The ID of the user this map belongs to.'
  },
  pageUrl: {
    type: String,
    label: 'The url for the destination page',
    optional: true
  },
  publicId: {
    type: String,
    label: 'The public key generated for the URL',
    optional: true,
    autoValue() {
      if (this.isInsert) return faker.internet.password();
    }
  },
  publicPrivateId: {
    type: String,
    label: 'The public key generated for the URL Private',
    optional: true,
    autoValue() {
      if (this.isInsert) return faker.internet.password();
    }
  },
  selectedButtons: {
    type: selectedButtonDataSchema,
    label: 'Indicates which buttons are being shown',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes([MAP_TYPES.LEAFLET], this.field('mapType').value),
        schemaInstance: this
      });
    }
  },
  settingsAdvancedMap: {
    type: settingsAdvancedMapDataSchema,
    label: 'Indicates which components are being shown',
    optional: true
  },

  shared: {
    type: Boolean,
    label: 'Flag that checks if map is shared',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  sharedPrivate: {
    type: Boolean,
    label: 'Flag that checks if map is shared Private',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  templates: {
    type: templatesSchema,
    label: 'An object where the components of map are to be saved',
    optional: true
  },
  updatedAt: {
    type: String,
    label: 'The date this map was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  withLocation: {
    type: Boolean,
    label: 'Flag that checks if map is location',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  withLocationPrivate: {
    type: Boolean,
    label: 'Flag that checks if map is location private',
    autoValue() {
      if (this.isInsert) return false;
    }
  }
});

Maps.attachSchema(Maps.schema);

export default Maps;
